import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
} from 'chart.js';
import { Bar, Pie, Doughnut, Line } from 'react-chartjs-2';
import jsPDF from 'jspdf';

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
    ArcElement,
    PointElement,
    LineElement
);

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5060/api';

const Reports = () => {
    const [salas, setSalas] = useState([]);
    const [selectedSala, setSelectedSala] = useState('');
    const [selectedDate, setSelectedDate] = useState('');
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(false);
    const reportRef = useRef(null);

    // PDF Section Selector state
    const [pdfSections, setPdfSections] = useState({
        summary: true,
        stations: true,
        podium: true,
        userTable: true
    });

    // Column selector state for user details table & PDF export
    const [visibleColumns, setVisibleColumns] = useState({
        name: true,
        ingreso: true,
        voto: true
    });

    useEffect(() => {
        const fetchSalas = async () => {
            try {
                const res = await axios.get(`${API_URL}/salas`);
                if (res.data.success) {
                    setSalas(res.data.data);
                }
            } catch (error) {
                console.error("Error fetching salas:", error);
            }
        };
        fetchSalas();
    }, []);

    useEffect(() => {
        const fetchStats = async () => {
            setLoading(true);
            try {
                const params = new URLSearchParams();
                if (selectedSala) params.append('sala_id', selectedSala);
                if (selectedDate) params.append('date', selectedDate);

                const res = await axios.get(`${API_URL}/reports/comprehensive?${params.toString()}`);
                if (res.data.success) {
                    setStats(res.data.data);

                    // Dynamically add active stations to visibleColumns selector
                    if (res.data.data.stations_stats) {
                        const newCols = { name: true, ingreso: true, voto: true };
                        res.data.data.stations_stats.forEach(st => {
                            newCols[`station_${st.id}`] = true;
                        });
                        setVisibleColumns(prev => ({ ...newCols, ...prev }));
                    }
                }
            } catch (error) {
                console.error("Error fetching stats:", error);
            }
            setLoading(false);
        };
        fetchStats();
    }, [selectedSala, selectedDate]);

    // Vector Text PDF Export (Native jsPDF drawing, NO canvas/image printing)
    const handleExportPDF = () => {
        if (!stats) return;

        try {
            const pdf = new jsPDF({ orientation: 'l', unit: 'mm', format: 'a4' });
            const pageWidth = pdf.internal.pageSize.getWidth(); // 297
            const pageHeight = pdf.internal.pageSize.getHeight(); // 210
            let y = 20;

            const checkPageBreak = (neededHeight = 15) => {
                if (y + neededHeight > pageHeight - 15) {
                    pdf.addPage();
                    y = 20;
                }
            };

            // HEADER
            pdf.setFont('helvetica', 'bold');
            pdf.setFontSize(18);
            pdf.setTextColor(30, 41, 59);
            pdf.text('Informe Estadístico General', pageWidth / 2, y, { align: 'center' });
            y += 7;

            pdf.setFontSize(11);
            pdf.setFont('helvetica', 'normal');
            pdf.setTextColor(100, 116, 139);
            pdf.text('Boehringer Ingelheim - Experiencia F1', pageWidth / 2, y, { align: 'center' });
            y += 6;

            const salaLabel = selectedSala ? `Sala ID: ${selectedSala}` : 'Todas las Salas';
            const fechaLabel = selectedDate ? `Fecha: ${selectedDate}` : 'Histórico Completo';
            pdf.setFontSize(9);
            pdf.text(`${salaLabel} | ${fechaLabel}`, pageWidth / 2, y, { align: 'center' });
            y += 12;

            let sectionNum = 1;

            // SECTION: RESUMEN DE USUARIOS NETOS
            if (pdfSections.summary) {
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(14);
                pdf.setTextColor(15, 23, 42);
                pdf.text(`${sectionNum}. Resumen General de Usuarios Netos`, 14, y);
                y += 8;
                sectionNum++;

                pdf.setFontSize(10);
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(51, 65, 85);
                pdf.text(`• Total Usuarios Registrados en BD: ${stats.total_registered || stats.total_users}`, 18, y); y += 6;
                pdf.text(`• Usuarios Excluidos (Staff / Pruebas): ${stats.excluded_users || 0}`, 18, y); y += 6;
                pdf.setFont('helvetica', 'bold');
                pdf.setTextColor(37, 99, 235);
                pdf.text(`• Usuarios Efectivos Netos (Base de cálculo): ${stats.total_users}`, 18, y); y += 6;
                pdf.setFont('helvetica', 'normal');
                pdf.setTextColor(51, 65, 85);
                pdf.text(`• Total Participantes (Ingresaron al sistema): ${stats.voted + stats.entered_not_voted}`, 18, y); y += 12;
            }

            // SECTION: CONSOLIDADO POR ESTACIÓN
            if (pdfSections.stations) {
                checkPageBreak(40);
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(14);
                pdf.setTextColor(15, 23, 42);
                pdf.text(`${sectionNum}. Consolidado de Participación por Estaciones`, 14, y);
                y += 8;
                sectionNum++;

                if (stats.stations_stats && stats.stations_stats.length > 0) {
                    stats.stations_stats.forEach((st, idx) => {
                        checkPageBreak(25);
                        pdf.setFont('helvetica', 'bold');
                        pdf.setFontSize(10);
                        pdf.setTextColor(30, 41, 59);
                        pdf.text(`Estación ${idx + 1}: ${st.name} (${st.code})`, 18, y);
                        y += 5;

                        pdf.setFont('helvetica', 'normal');
                        pdf.setFontSize(9);
                        pdf.setTextColor(71, 85, 105);
                        pdf.text(`   - Usuarios Completados: ${st.completed_users} de ${st.effective_total_users} netos`, 18, y);
                        pdf.setFont('helvetica', 'bold');
                        pdf.setTextColor(79, 70, 229);
                        pdf.text(` |  ${st.completion_percentage}% Completado`, 115, y);
                        y += 6;

                        if (st.has_stages && st.stages && st.stages.length > 0) {
                            st.stages.forEach(sg => {
                                checkPageBreak(5);
                                pdf.setFont('helvetica', 'normal');
                                pdf.setTextColor(100, 116, 139);
                                pdf.text(`       * ${sg.name}: ${sg.completed_users} usuarios (${sg.completion_percentage}%)`, 22, y);
                                y += 5;
                            });
                        }
                        y += 2;
                    });
                }
                y += 8;
            }

            // SECTION: PODIO DE GANADORES Y TOP 20
            if (pdfSections.podium) {
                checkPageBreak(50);
                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(14);
                pdf.setTextColor(15, 23, 42);
                pdf.text(`${sectionNum}. Podio de Ganadores y Top 20 Mejores Puntajes`, 14, y);
                y += 8;
                sectionNum++;

                if (stats.top_scores && stats.top_scores.length > 0) {
                    // Header table for Top 20
                    pdf.setFillColor(238, 242, 255);
                    pdf.rect(14, y, pageWidth - 28, 8, 'F');
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(8.5);
                    pdf.setTextColor(67, 56, 202);

                    pdf.text('Pos', 16, y + 5.5);
                    pdf.text('Participante', 28, y + 5.5);
                    pdf.text('Estaciones', 110, y + 5.5);
                    pdf.text('P. Destreza', 142, y + 5.5);
                    pdf.text('Puntaje Total', 170, y + 5.5);
                    y += 9;

                    pdf.setFont('helvetica', 'normal');
                    pdf.setFontSize(8);

                    stats.top_scores.forEach((rank, idx) => {
                        checkPageBreak(7);
                        if (y === 20) {
                            pdf.setFillColor(238, 242, 255);
                            pdf.rect(14, y, pageWidth - 28, 8, 'F');
                            pdf.setFont('helvetica', 'bold');
                            pdf.setFontSize(8.5);
                            pdf.setTextColor(67, 56, 202);
                            pdf.text('Pos', 16, y + 5.5);
                            pdf.text('Participante', 28, y + 5.5);
                            pdf.text('Estaciones', 110, y + 5.5);
                            pdf.text('P. Destreza', 142, y + 5.5);
                            pdf.text('Puntaje Total', 170, y + 5.5);
                            y += 9;
                            pdf.setFont('helvetica', 'normal');
                            pdf.setFontSize(8);
                        }

                        const posStr = `${idx + 1}°`;
                        const pName = `${rank.name || ''} ${rank.last_name || ''}`.trim() || rank.email;

                        if (idx < 3) pdf.setFont('helvetica', 'bold');
                        else pdf.setFont('helvetica', 'normal');

                        if (idx === 0) pdf.setTextColor(217, 119, 6); // Gold
                        else if (idx === 1) pdf.setTextColor(100, 116, 139); // Silver
                        else if (idx === 2) pdf.setTextColor(180, 83, 9); // Bronze
                        else pdf.setTextColor(51, 65, 85);

                        pdf.text(posStr, 16, y + 4.5);
                        pdf.text(pName.length > 38 ? pName.substring(0, 38) + '..' : pName, 28, y + 4.5);
                        pdf.text(String(rank.stations_visited || 0), 115, y + 4.5);
                        pdf.text(String(rank.skill_points || 0), 146, y + 4.5);

                        pdf.setFont('helvetica', 'bold');
                        pdf.text(String(rank.total_points || 0), 173, y + 4.5);

                        pdf.setDrawColor(241, 245, 249);
                        pdf.line(14, y + 6, pageWidth - 14, y + 6);
                        y += 6.5;
                    });
                } else {
                    pdf.setFont('helvetica', 'italic');
                    pdf.setFontSize(9);
                    pdf.setTextColor(100, 116, 139);
                    pdf.text('No hay registros de puntos suficientes para el podio.', 18, y);
                    y += 8;
                }
                y += 8;
            }

            // SECTION: TABLA DE DETALLE DE USUARIOS CON ESTACIONES
            if (pdfSections.userTable) {
                if (y > pageHeight - 60) {
                    pdf.addPage();
                    y = 20;
                }

                pdf.setFont('helvetica', 'bold');
                pdf.setFontSize(14);
                pdf.setTextColor(15, 23, 42);
                pdf.text(`${sectionNum}. Detalle de Usuarios y Participación por Estación`, 14, y);
                y += 8;
                sectionNum++;

                // Prepare columns dynamically from visibleColumns
                const activeCols = [];
                if (visibleColumns.name) activeCols.push({ id: 'name', label: 'Usuario', width: 50 });
                if (visibleColumns.ingreso) activeCols.push({ id: 'ingreso', label: 'Ingreso', width: 20 });
                if (visibleColumns.voto) activeCols.push({ id: 'voto', label: 'Voto', width: 20 });

                if (stats.stations_stats) {
                    stats.stations_stats.forEach(st => {
                        if (visibleColumns[`station_${st.id}`]) {
                            activeCols.push({
                                id: `station_${st.id}`,
                                label: st.name.length > 12 ? st.name.substring(0, 12) + '..' : st.name,
                                width: 24
                            });
                        }
                    });
                }

                // Draw Table Header
                const drawTableHeader = () => {
                    pdf.setFillColor(241, 245, 249);
                    pdf.rect(14, y, pageWidth - 28, 8, 'F');
                    pdf.setFont('helvetica', 'bold');
                    pdf.setFontSize(8);
                    pdf.setTextColor(51, 65, 85);

                    let currentX = 16;
                    activeCols.forEach(col => {
                        pdf.text(col.label, currentX, y + 5.5);
                        currentX += col.width;
                    });
                    y += 9;
                };

                drawTableHeader();

                pdf.setFont('helvetica', 'normal');
                pdf.setFontSize(7.5);
                pdf.setTextColor(51, 65, 85);

                if (stats.user_details && stats.user_details.length > 0) {
                    stats.user_details.forEach(u => {
                        checkPageBreak(8);
                        if (y === 20) drawTableHeader();

                        let currentX = 16;
                        activeCols.forEach(col => {
                            let textVal = 'No';
                            if (col.id === 'name') textVal = `${u.name || ''} ${u.last_name || ''}`.trim() || u.email;
                            else if (col.id === 'sala') textVal = u.sala_name || 'Sin Sala';
                            else if (col.id === 'ingreso') textVal = u.ingreso || 'No';
                            else if (col.id === 'firmo') textVal = u.firmo || 'No';
                            else if (col.id === 'voto') textVal = u.voto || 'No';
                            else if (col.id.startsWith('station_')) {
                                textVal = u.stations_status ? (u.stations_status[col.id] || 'No') : 'No';
                            }

                            // Truncate text if exceeds col width
                            const strVal = String(textVal);
                            const safeStr = strVal.length > 18 ? strVal.substring(0, 18) + '..' : strVal;

                            if (safeStr === 'Sí') pdf.setTextColor(16, 185, 129);
                            else if (safeStr === 'No') pdf.setTextColor(239, 68, 68);
                            else pdf.setTextColor(51, 65, 85);

                            pdf.text(safeStr, currentX, y + 4.5);
                            currentX += col.width;
                        });

                        pdf.setDrawColor(241, 245, 249);
                        pdf.line(14, y + 6, pageWidth - 14, y + 6);
                        y += 6.5;
                    });
                }
            }

            pdf.save(`Reporte_Estadisticas_${new Date().toISOString().split('T')[0]}.pdf`);
        } catch (error) {
            console.error("Error generating text PDF:", error);
            alert("Ocurrió un error al exportar el reporte en formato PDF de texto.");
        }
    };

    const toggleColumn = (colId) => {
        setVisibleColumns(prev => ({
            ...prev,
            [colId]: !prev[colId]
        }));
    };

    if (!stats) return <div className="p-8 text-center">Cargando...</div>;

    const totalSignature = stats.with_signature + stats.without_signature;
    const withSignaturePct = totalSignature ? ((stats.with_signature / totalSignature) * 100).toFixed(1) : 0;
    const withoutSignaturePct = totalSignature ? ((stats.without_signature / totalSignature) * 100).toFixed(1) : 0;

    const signatureData = {
        labels: [`Con Firma: ${stats.with_signature} (${withSignaturePct}%)`, `Sin Firma: ${stats.without_signature} (${withoutSignaturePct}%)`],
        datasets: [{
            data: [stats.with_signature, stats.without_signature],
            backgroundColor: ['#10b981', '#f43f5e'],
            borderWidth: 1,
        }]
    };

    const totalParticipation = stats.voted + stats.entered_not_voted + stats.not_entered_not_voted;
    const votedPct = totalParticipation ? ((stats.voted / totalParticipation) * 100).toFixed(1) : 0;
    const enteredNotVotedPct = totalParticipation ? ((stats.entered_not_voted / totalParticipation) * 100).toFixed(1) : 0;
    const notEnteredNotVotedPct = totalParticipation ? ((stats.not_entered_not_voted / totalParticipation) * 100).toFixed(1) : 0;

    const participationData = {
        labels: [
            `Votaron: ${stats.voted} (${votedPct}%)`,
            `Ingresaron pero NO Votaron: ${stats.entered_not_voted} (${enteredNotVotedPct}%)`,
            `NO Ingresaron y NO Votaron: ${stats.not_entered_not_voted} (${notEnteredNotVotedPct}%)`
        ],
        datasets: [{
            data: [stats.voted, stats.entered_not_voted, stats.not_entered_not_voted],
            backgroundColor: ['#3b82f6', '#f59e0b', '#64748b'],
            borderWidth: 1,
        }]
    };

    const totalRegistrados = stats.total_users;
    const totalIngresaron = stats.voted + stats.entered_not_voted;
    const funnelIngresaronPct = totalRegistrados ? ((totalIngresaron / totalRegistrados) * 100).toFixed(1) : 0;
    const funnelVotaronPct = totalRegistrados ? ((stats.voted / totalRegistrados) * 100).toFixed(1) : 0;

    const funnelData = {
        labels: [
            `Total Registrados (${totalRegistrados} - 100%)`,
            `Ingresaron (${totalIngresaron} - ${funnelIngresaronPct}%)`,
            `Votaron (${stats.voted} - ${funnelVotaronPct}%)`
        ],
        datasets: [{
            label: 'Cantidad de Usuarios',
            data: [
                stats.total_users,
                stats.voted + stats.entered_not_voted,
                stats.voted
            ],
            backgroundColor: ['#8b5cf6', '#0ea5e9', '#14b8a6'],
            borderWidth: 1,
        }]
    };

    const PAGE_NAME_MAP = {
        'home': 'INICIO',
        'circuito': 'MAPA CIRCUITO',
        'podium': 'PODIO DE GANADORES',
        'podio': 'PODIO DE GANADORES',
        'selfie': 'MARCO DE FOTOS / SELFIE',
        'concentrese': 'CONCÉNTRESE',
        'laberinto': 'LABERINTO',
        'escanear-puntos': 'REGISTRO PUNTOS QR',
        'agenda': 'AGENDA',
        'speakers': 'CONFERENCISTAS',
        'streaming': 'EN VIVO',
        'profile': 'MI PERFIL',
        'welcome': 'BIENVENIDA (CARTA LOGÍSTICA)',
        'thanks': 'AGRADECIMIENTO',
        'certificate': 'CERTIFICADO',
        'questions-expert': 'PREGUNTAS AL EXPOSITOR',
        'questions_private': 'PREGUNTAS AL EXPOSITOR',
        'interactiva': 'TRIVIA EN VIVO',
        'gallery': 'GALERÍA DE FOTOS',
        'event-gallery': 'GALERÍA DE FOTOS',
        'staff-gallery': 'GALERÍA STAFF',
        'staff-questions': 'MODERAR PREGUNTAS (STAFF)',
        'scanner': 'ESCANEAR (STAFF)',
        'staff-qr-generator': 'GENERAR QR PUNTOS (STAFF)',
        'interactiva-gestion': 'GESTIÓN TRIVIA (STAFF)',
        'login': 'INICIO DE SESIÓN',
        'register': 'REGISTRO'
    };

    const chartOptions = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'bottom',
                labels: {
                    font: { size: 14, weight: 'bold' },
                    color: '#333'
                }
            }
        }
    };

    const barOptions = {
        ...chartOptions,
        indexAxis: 'y',
        plugins: {
            ...chartOptions.plugins,
            title: {
                display: true,
                text: 'Embudo de Conversión',
                font: { size: 16 }
            }
        }
    };

    return (
        <div className="w-full">
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Reporte Completo de Estadísticas</h1>
                <span>Boehringer Ingelheim - Experiencia F1</span>
                <button
                    onClick={handleExportPDF}
                    className="bg-red-600 hover:bg-red-700 text-white px-6 py-2.5 rounded-lg font-bold shadow-md transition-colors flex items-center"
                >
                    <span className="mr-2">📄</span> Exportar PDF (Texto Vectorial)
                </button>
            </div>

            {/* Filters */}
            <div className="bg-white p-4 rounded-xl shadow-md mb-8 flex flex-wrap gap-4 items-end justify-between">
                <div className="flex gap-4">
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Filtrar por Sala</label>
                        <select
                            value={selectedSala}
                            onChange={(e) => setSelectedSala(e.target.value)}
                            className="w-64 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="">Todas las Salas</option>
                            {salas.map(s => (
                                <option key={s.id} value={s.id}>{s.name}</option>
                            ))}
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-1">Filtrar por Fecha</label>
                        <input
                            type="date"
                            value={selectedDate}
                            onChange={(e) => setSelectedDate(e.target.value)}
                            className="w-48 p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>

                {/* Column Selector for User Table & PDF */}
                <div className="flex flex-col gap-3">
                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                        <span className="block text-xs font-bold text-slate-600 uppercase mb-2">Secciones a Incluir en PDF:</span>
                        <div className="flex flex-wrap gap-4 text-xs font-bold">
                            <label className="flex items-center gap-1 cursor-pointer text-slate-700">
                                <input type="checkbox" checked={pdfSections.summary} onChange={() => setPdfSections(p => ({ ...p, summary: !p.summary }))} /> 1. Resumen General
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer text-indigo-700">
                                <input type="checkbox" checked={pdfSections.stations} onChange={() => setPdfSections(p => ({ ...p, stations: !p.stations }))} /> 2. Consolidado por Estación
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer text-amber-700">
                                <input type="checkbox" checked={pdfSections.podium} onChange={() => setPdfSections(p => ({ ...p, podium: !p.podium }))} /> 3. Podio y Top 20 Puntajes
                            </label>
                            <label className="flex items-center gap-1 cursor-pointer text-purple-700">
                                <input type="checkbox" checked={pdfSections.userTable} onChange={() => setPdfSections(p => ({ ...p, userTable: !p.userTable }))} /> 4. Detalle de Usuarios
                            </label>
                        </div>
                    </div>

                    <div className="bg-slate-50 p-3 rounded-lg border border-slate-200">
                        <span className="block text-xs font-bold text-slate-600 uppercase mb-2">Columnas Visibles de Usuarios (Vista y PDF):</span>
                        <div className="flex flex-wrap gap-3 text-xs">
                            <label className="flex items-center gap-1 font-semibold cursor-pointer">
                                <input type="checkbox" checked={!!visibleColumns.name} onChange={() => toggleColumn('name')} /> Nombre
                            </label>
                            <label className="flex items-center gap-1 font-semibold cursor-pointer">
                                <input type="checkbox" checked={!!visibleColumns.ingreso} onChange={() => toggleColumn('ingreso')} /> Ingreso
                            </label>
                            <label className="flex items-center gap-1 font-semibold cursor-pointer">
                                <input type="checkbox" checked={!!visibleColumns.voto} onChange={() => toggleColumn('voto')} /> Voto
                            </label>
                            {stats.stations_stats && stats.stations_stats.map(st => (
                                <label key={st.id} className="flex items-center gap-1 font-bold text-purple-700 cursor-pointer">
                                    <input type="checkbox" checked={!!visibleColumns[`station_${st.id}`]} onChange={() => toggleColumn(`station_${st.id}`)} /> {st.name}
                                </label>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {/* Report Content */}
            <div ref={reportRef} className="bg-gray-100 rounded-xl relative">

                {/* PAGE 1 */}
                <div className="pdf-page w-full bg-white p-8" style={{ minHeight: '297mm' }}>
                    <div className="text-center mb-10">
                        <h2 className="text-4xl font-black text-slate-800 uppercase tracking-wide">Informe Estadístico</h2>
                        <span className="text-slate-500 font-semibold">Boehringer Ingelheim - Experiencia F1</span>
                        <p className="text-xl text-slate-500 mt-2">
                            {selectedSala ? `Sala ID: ${selectedSala}` : 'Todas las Salas'} |
                            {selectedDate ? ` Fecha: ${selectedDate}` : ' Histórico Completo'}
                        </p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                        <div className="bg-slate-50 border border-slate-200 p-5 rounded-2xl shadow-sm text-center">
                            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">Total Registrados</h3>
                            <p className="text-4xl font-black text-slate-700 mt-2">{stats.total_registered || stats.total_users}</p>
                            <span className="text-[11px] text-slate-400 font-semibold block mt-1">Base de Datos</span>
                        </div>
                        <div className="bg-amber-50 border border-amber-200 p-5 rounded-2xl shadow-sm text-center">
                            <h3 className="text-sm font-bold text-amber-700 uppercase tracking-wider">Excluidos (Staff/Pruebas)</h3>
                            <p className="text-4xl font-black text-amber-600 mt-2">{stats.excluded_users || 0}</p>
                            <span className="text-[11px] text-amber-600 font-bold block mt-1">Omitidos de Métricas</span>
                        </div>
                        <div className="bg-blue-50 border border-blue-200 p-5 rounded-2xl shadow-sm text-center">
                            <h3 className="text-sm font-bold text-blue-700 uppercase tracking-wider">Usuarios Efectivos Netos</h3>
                            <p className="text-4xl font-black text-blue-600 mt-2">{stats.total_users}</p>
                            <span className="text-[11px] text-blue-600 font-bold block mt-1">Registrados - Omitidos</span>
                        </div>
                        <div className="bg-slate-50 border border-slate-200 p-5 rounded-2xl shadow-sm text-center">
                            <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wider">Total Participantes</h3>
                            <p className="text-4xl font-black text-indigo-600 mt-2">{stats.voted + stats.entered_not_voted}</p>
                            <span className="text-[11px] text-slate-400 font-semibold block mt-1">Ingresaron al sistema</span>
                        </div>
                    </div>
                </div>

                {/* PAGE 3: Consolidado por Estación */}
                <div className="pdf-page w-full bg-white p-8 mt-4" style={{ minHeight: '297mm' }}>
                    <div className="mb-12">
                        <div className="text-center mb-6">
                            <h3 className="text-2xl font-bold text-slate-800 uppercase tracking-wide">Consolidado General por Estación</h3>
                            <p className="text-sm text-slate-500 font-semibold mt-1">
                                Porcentaje de completados calculado sobre <span className="font-extrabold text-blue-600">{stats.total_users} Usuarios Efectivos</span> (Registrados - Omitidos).
                            </p>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
                            {stats.stations_stats && stats.stations_stats.map((st, idx) => (
                                <div key={idx} className="bg-slate-50 border border-slate-200 rounded-2xl p-6 shadow-sm">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <h4 className="font-black text-slate-800 text-lg">{st.name}</h4>
                                            <span className="text-[11px] font-mono text-purple-600 bg-purple-50 px-2 py-0.5 rounded-md border border-purple-100">
                                                {st.code}
                                            </span>
                                        </div>
                                        <div className="text-right">
                                            <span className="text-3xl font-black text-indigo-600">{st.completion_percentage}%</span>
                                            <span className="text-xs text-slate-400 block font-bold">Completado</span>
                                        </div>
                                    </div>

                                    {/* Progress Bar */}
                                    <div className="w-full bg-slate-200 h-3 rounded-full overflow-hidden mb-3">
                                        <div 
                                            className="bg-indigo-600 h-full rounded-full transition-all duration-500"
                                            style={{ width: `${Math.min(100, st.completion_percentage)}%` }}
                                        ></div>
                                    </div>

                                    <div className="flex justify-between items-center text-xs font-bold text-slate-600 pt-2 border-t border-slate-200">
                                        <span>Completados: <strong className="text-emerald-600">{st.completed_users}</strong> / {st.effective_total_users} netos</span>
                                        {st.has_stages && (
                                            <span className="text-purple-600 bg-purple-100 px-2 py-0.5 rounded-full font-extrabold text-[10px]">
                                                {st.stages?.length || 4} Etapas
                                            </span>
                                        )}
                                    </div>

                                    {/* Stages Breakdown */}
                                    {st.has_stages && st.stages && st.stages.length > 0 && (
                                        <div className="mt-4 pt-3 border-t border-slate-200/80 space-y-2">
                                            <h5 className="text-[11px] font-black uppercase text-slate-500 tracking-wider">Desglose por Etapa (Días):</h5>
                                            <div className="grid grid-cols-2 gap-2">
                                                {st.stages.map((sg, sgIdx) => (
                                                    <div key={sgIdx} className="bg-white p-2.5 rounded-xl border border-slate-100 flex justify-between items-center">
                                                        <div>
                                                            <span className="text-[11px] font-bold text-slate-700 block truncate">{sg.name}</span>
                                                            <span className="text-[10px] text-slate-400">{sg.completed_users} usuarios</span>
                                                        </div>
                                                        <span className="text-xs font-black text-purple-600 bg-purple-50 px-2 py-1 rounded-lg">
                                                            {sg.completion_percentage}%
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>

                {/* PODIO Y TOP 20 MEJORES PUNTAJES */}
                <div className="w-full bg-white p-8 mt-4 rounded-xl shadow-sm">
                    <div className="text-center mb-6">
                        <h3 className="text-2xl font-bold text-slate-800 uppercase tracking-wide">
                            🏆 Podio de Ganadores y Top 20 Mejores Puntajes
                        </h3>
                        <p className="text-sm text-slate-500 font-semibold mt-1">
                            Clasificación en tiempo real basada en participación y destreza acumulada.
                        </p>
                    </div>

                    {stats.top_scores && stats.top_scores.length > 0 ? (
                        <div className="overflow-x-auto">
                            <table className="min-w-full bg-white border border-gray-200">
                                <thead className="bg-indigo-50">
                                    <tr>
                                        <th className="py-2.5 px-4 border-b font-bold text-indigo-900 text-center text-xs uppercase">Pos</th>
                                        <th className="py-2.5 px-4 border-b font-bold text-indigo-900 text-left text-xs uppercase">Participante</th>
                                        <th className="py-2.5 px-4 border-b font-bold text-indigo-900 text-center text-xs uppercase">Estaciones Visitadas</th>
                                        <th className="py-2.5 px-4 border-b font-bold text-indigo-900 text-center text-xs uppercase">Puntos Destreza</th>
                                        <th className="py-2.5 px-4 border-b font-extrabold text-indigo-900 text-center text-xs uppercase bg-indigo-100">Puntaje Total</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {stats.top_scores.map((rank, idx) => (
                                        <tr key={idx} className={`hover:bg-indigo-50/50 transition-colors ${idx < 3 ? 'bg-amber-50/30 font-bold' : ''}`}>
                                            <td className="py-2.5 px-4 border-b text-center text-sm font-black">
                                                {idx === 0 && <span className="text-amber-500 text-base">🥇 1°</span>}
                                                {idx === 1 && <span className="text-slate-400 text-base">🥈 2°</span>}
                                                {idx === 2 && <span className="text-amber-700 text-base">🥉 3°</span>}
                                                {idx >= 3 && <span className="text-slate-500">{idx + 1}°</span>}
                                            </td>
                                            <td className="py-2.5 px-4 border-b text-sm text-gray-800">
                                                {rank.name} {rank.last_name}
                                            </td>
                                            <td className="py-2.5 px-4 border-b text-center text-sm font-semibold text-slate-700">
                                                {rank.stations_visited}
                                            </td>
                                            <td className="py-2.5 px-4 border-b text-center text-sm font-semibold text-purple-700">
                                                {rank.skill_points} pts
                                            </td>
                                            <td className="py-2.5 px-4 border-b text-center text-base font-black text-indigo-600 bg-indigo-50/50">
                                                {rank.total_points} pts
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    ) : (
                        <div className="p-8 text-center text-gray-500 font-semibold">
                            No hay suficientes datos de puntaje registrados aún.
                        </div>
                    )}
                </div>

                {/* USER DETAILS TABLE WITH STATIONS CHECK & DYNAMIC COLUMNS */}
                <div className="w-full bg-white p-8 mt-4 rounded-xl shadow-sm overflow-x-auto">
                    <h3 className="text-2xl font-bold text-slate-800 text-center mb-6">
                        Detalle de Usuarios y Participación por Estación
                    </h3>
                    <table className="min-w-full bg-white border border-gray-200">
                        <thead className="bg-slate-100">
                            <tr>
                                {visibleColumns.name && <th className="py-2.5 px-4 border-b font-semibold text-gray-700 text-left text-xs uppercase">Nombre</th>}
                                {visibleColumns.ingreso && <th className="py-2.5 px-4 border-b font-semibold text-gray-700 text-center text-xs uppercase">Ingreso</th>}
                                {visibleColumns.voto && <th className="py-2.5 px-4 border-b font-semibold text-gray-700 text-center text-xs uppercase">Voto</th>}
                                {stats.stations_stats && stats.stations_stats.map(st => (
                                    visibleColumns[`station_${st.id}`] && (
                                        <th key={st.id} className="py-2.5 px-3 border-b font-bold text-purple-800 text-center text-xs uppercase bg-purple-50">
                                            {st.name}
                                        </th>
                                    )
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {stats.user_details && stats.user_details.map((u, idx) => (
                                <tr key={idx} className="hover:bg-slate-50 transition-colors">
                                    {visibleColumns.name && (
                                        <td className="py-2 px-4 border-b text-sm text-gray-800 font-medium">
                                            {u.name} {u.last_name}
                                        </td>
                                    )}
                                    {visibleColumns.ingreso && (
                                        <td className="py-2 px-4 border-b text-center text-sm font-bold">
                                            <span className={u.ingreso === 'Sí' ? 'text-emerald-600' : 'text-rose-500'}>{u.ingreso}</span>
                                        </td>
                                    )}
                                    {visibleColumns.voto && (
                                        <td className="py-2 px-4 border-b text-center text-sm font-bold">
                                            <span className={u.voto === 'Sí' ? 'text-emerald-600' : 'text-rose-500'}>{u.voto}</span>
                                        </td>
                                    )}
                                    {stats.stations_stats && stats.stations_stats.map(st => (
                                        visibleColumns[`station_${st.id}`] && (
                                            <td key={st.id} className="py-2 px-3 border-b text-center text-sm font-bold">
                                                <span className={u.stations_status && u.stations_status[`station_${st.id}`] === 'Sí' ? 'bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full text-xs' : 'bg-slate-100 text-slate-400 px-2 py-0.5 rounded-full text-xs'}>
                                                    {u.stations_status ? u.stations_status[`station_${st.id}`] : 'No'}
                                                </span>
                                            </td>
                                        )
                                    ))}
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

            </div>
        </div>
    );
};

export default Reports;
